import React from "react";
import {Helmet} from "react-helmet";
import useSiteSettings from "~utils/useSiteSettings";

const SITE_URL = "https://misura.com.au";

// Schema.org wants a single machine-readable value; the CMS stores numbers in
// human-readable form ("+61 3 9429 1221", sometimes with a leading space).
const formatTelephone = (value) => String(value).replace(/\s+/g, "");

const toPostalAddress = (showroom) => ({
  "@type": "PostalAddress",
  streetAddress: showroom.address,
  ...(showroom.city && {addressLocality: showroom.city}),
  ...(showroom.state && {addressRegion: showroom.state}),
  ...(showroom.postCode && {postalCode: showroom.postCode}),
  addressCountry: "AU",
});

/**
 * Global Organization JSON-LD. Rendered once from Layout so it appears on every
 * page, which is what Google expects for site-wide brand signals.
 *
 * Everything is sourced from Sanity rather than hardcoded so it cannot drift
 * from the site content — the one exception is `logo`, which has no CMS field.
 */
const OrganizationSchema = () => {
  const {
    abn,
    facebookUrl,
    instagramUrl,
    linkedInUrl,
    pinterestUrl,
    showrooms,
    siteMetaDescription,
  } = useSiteSettings();

  const sameAs = [facebookUrl, instagramUrl, linkedInUrl, pinterestUrl].filter(Boolean);

  const locations = (showrooms ?? []).filter((showroom) => showroom?.address);
  const addresses = locations.map(toPostalAddress);

  const contactPoints = (showrooms ?? [])
    .filter((showroom) => showroom?.phoneNumber)
    .map((showroom) => ({
      "@type": "ContactPoint",
      telephone: formatTelephone(showroom.phoneNumber),
      contactType: "customer service",
      areaServed: "AU",
      availableLanguage: "English",
    }));

  const schema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    // Stable @id so later schema (WebSite, Product, Article) can reference this
    // organization instead of redeclaring it.
    "@id": `${SITE_URL}/#organization`,
    name: "MISURA",
    url: `${SITE_URL}/`,
    logo: {
      "@type": "ImageObject",
      url: `${SITE_URL}/icons/icon-512x512.png`,
      width: 512,
      height: 512,
    },
    ...(siteMetaDescription && {description: siteMetaDescription}),
    ...(sameAs.length > 0 && {sameAs}),
    // Google prefers a bare object when there is only one location.
    ...(addresses.length > 0 && {address: addresses.length === 1 ? addresses[0] : addresses}),
    ...(contactPoints.length > 0 && {contactPoint: contactPoints}),
    ...(abn && {
      identifier: {
        "@type": "PropertyValue",
        name: "ABN",
        value: abn,
      },
    }),
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
};

export default OrganizationSchema;
